import {
  clearRefinements,
  configure,
  currentRefinements,
  dynamicWidgets,
  hits,
  pagination,
  panel,
  refinementList,
  stats,
  toggleRefinement,
} from 'instantsearch.js/es/widgets';
import { connectRange, connectSearchBox, connectSortBy } from 'instantsearch.js/es/connectors';
import { renderEmptyState, renderProductHit, renderStatsText, resetHitRenderIndex } from './templates';

const SELECTORS = {
  stats: '[data-instant-search-stats]',
  currentRefinements: '[data-instant-search-current-refinements]',
  clearRefinements: '[data-instant-search-clear-refinements]',
  sortBy: '[data-instant-search-sort-by]',
  sortShell: '[data-instant-search-sort-shell]',
  sidebar: '[data-instant-search-sidebar]',
  hits: '[data-instant-search-hits]',
  pagination: '[data-instant-search-pagination]',
};

const createVirtualSearchBox = connectSearchBox(() => {});
const CATEGORY_TAG_PREFIX_PATTERN = /^\[Category:\s*(.+?)\]$/;
let hasBoundSwatchEvents = false;

function escapeHtml(value) {
  if (value == null) {
    return '';
  }

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderPanelHeader(label) {
  return `
    <button type="button" class="search-page__filter-trigger" aria-expanded="true">
      <span class="search-page__filter-trigger-label">${escapeHtml(label)}</span>
      <svg class="search-page__filter-trigger-icon" aria-hidden="true">
        <use xlink:href="#icon-prev"></use>
      </svg>
    </button>
  `;
}

function updateSwatchableCard(card, swatchTrigger) {
  if (!card || !swatchTrigger) {
    return;
  }

  const primaryImage = card.querySelector('.search-hit-card__image--primary');
  const hoverImage = card.querySelector('.search-hit-card__image--hover');
  const nextPrimarySrc = swatchTrigger.dataset.image || '';
  const nextHoverSrc = swatchTrigger.dataset.hoverImage || '';
  const nextAlt = swatchTrigger.dataset.color || primaryImage?.alt || '';

  if (primaryImage && nextPrimarySrc) {
    primaryImage.removeAttribute('srcset');
    primaryImage.src = nextPrimarySrc;
    primaryImage.alt = nextAlt;
  }

  if (hoverImage) {
    if (nextHoverSrc && nextHoverSrc !== nextPrimarySrc) {
      hoverImage.src = nextHoverSrc;
      hoverImage.hidden = false;
    } else {
      hoverImage.hidden = true;
      hoverImage.removeAttribute('src');
    }
  }

  card.querySelectorAll('[data-swatch-trigger]').forEach((button) => {
    const isActive = button === swatchTrigger;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });

  const nextPrice = swatchTrigger.dataset.price || '';
  const nextComparePrice = swatchTrigger.dataset.compareAtPrice || '';
  const priceCurrent = card.querySelector('.search-hit-card__price-current');

  if (priceCurrent && nextPrice) {
    priceCurrent.textContent = nextPrice;
    priceCurrent.classList.toggle('search-hit-card__price-current--sale', Boolean(nextComparePrice));

    let compareEl = card.querySelector('.search-hit-card__price-compare');

    if (nextComparePrice) {
      if (!compareEl) {
        compareEl = document.createElement('span');
        compareEl.className = 'search-hit-card__price-compare';
        priceCurrent.parentNode.appendChild(compareEl);
      }
      compareEl.textContent = nextComparePrice;
      compareEl.hidden = false;
    } else if (compareEl) {
      compareEl.hidden = true;
    }
  }
}

function bindSwatchEvents(hitsContainer) {
  if (hasBoundSwatchEvents || !hitsContainer) {
    return;
  }

  const handleSwatchInteraction = (event) => {
    const swatchTrigger = event.target.closest('[data-swatch-trigger]');

    if (!swatchTrigger || !hitsContainer.contains(swatchTrigger)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.type === 'click') {
      updateSwatchableCard(swatchTrigger.closest('[data-swatchable-card]'), swatchTrigger);
    }
  };

  hitsContainer.addEventListener('click', handleSwatchInteraction);
  hitsContainer.addEventListener('mousedown', handleSwatchInteraction);
  hasBoundSwatchEvents = true;
}

let _activeColorGroups = [];

function createColorRefinementTracker(config) {
  return {
    $$type: 'custom.colorRefinementTracker',
    init() {},
    render({ helper }) {
      const disjunctive = helper.state.disjunctiveFacetsRefinements || {};
      const conjunctive = helper.state.facetsRefinements || {};

      _activeColorGroups =
        disjunctive[config.attributes.color] ||
        disjunctive['meta.custom.color_group'] ||
        conjunctive[config.attributes.color] ||
        conjunctive['meta.custom.color_group'] ||
        [];
    },
    dispose() {
      _activeColorGroups = [];
    },
  };
}

function createNoResultsToggle(root) {
  return {
    $$type: 'custom.noResultsToggle',
    init() {},
    render({ results }) {
      const isEmpty = !results || results.nbHits === 0;
      root.classList.toggle('search-page--no-results', isEmpty);
    },
    dispose() {
      root.classList.remove('search-page--no-results');
    },
  };
}

function createInsightsIndexTracker(root, config) {
  return {
    $$type: 'custom.insightsIndexTracker',
    init() {
      root.setAttribute('data-insights-index', config.indexName);
    },
    render({ results }) {
      const currentIndex = (results && results.index) || config.indexName;
      root.setAttribute('data-insights-index', currentIndex);
    },
    dispose() {
      root.removeAttribute('data-insights-index');
    },
  };
}

let _lastViewedQueryID = null;
let _lastViewedPage = null;

function createHitsViewedPusher() {
  return {
    $$type: 'custom.hitsViewedPusher',
    init() {},
    render({ results }) {
      if (!results || results.nbHits === 0) return;

      const queryID = results.queryID || '';
      const page = results.page;

      if (queryID === _lastViewedQueryID && page === _lastViewedPage) return;

      _lastViewedQueryID = queryID;
      _lastViewedPage = page;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'Hits Viewed' });
    },
    dispose() {
      _lastViewedQueryID = null;
      _lastViewedPage = null;
    },
  };
}

function hasFacetResults(results, attribute) {
  if (!results || !attribute) {
    return false;
  }

  try {
    const facetValues = results.getFacetValues(attribute);

    return Array.isArray(facetValues) ? facetValues.length > 0 : Boolean(facetValues);
  } catch (error) {
    void error;
    return false;
  }
}

function getCategoryTagLabel(value) {
  if (typeof value !== 'string') {
    return value;
  }

  const match = value.match(CATEGORY_TAG_PREFIX_PATTERN);

  return match ? match[1].trim() : null;
}

function getFacetItemValue(item) {
  if (item == null) {
    return item;
  }

  if (typeof item === 'string') {
    return item;
  }

  return item.label || item.name || item.value || '';
}

function hasRangeResults(results, attribute) {
  if (!results || !attribute) {
    return false;
  }

  try {
    const facetStats = results.getFacetStats(attribute);

    return Boolean(facetStats && (facetStats.min != null || facetStats.max != null));
  } catch (error) {
    void error;
    return false;
  }
}

function humanizeAttribute(attribute) {
  if (!attribute) {
    return '';
  }

  return attribute
    .split('.')
    .pop()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\bgroup\b/gi, '')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getAttributeLabel(config, attribute) {
  const { attributes, labels } = config;
  const labelMap = {
    [attributes.productType]: labels.productType,
    [attributes.type]: labels.type,
    [attributes.color]: labels.color,
    [attributes.size]: labels.size,
    [attributes.availability]: labels.availability,
    [attributes.price]: labels.price,
  };

  return labelMap[attribute] || humanizeAttribute(attribute);
}

function resolveWidgetType(attribute, config) {
  if (attribute === config.attributes.price || attribute === 'price') {
    return 'rangeInput';
  }

  if (attribute === config.attributes.availability || attribute === 'inventory_available') {
    return 'toggleRefinement';
  }

  return 'refinementList';
}

function getPanelKey(attribute) {
  return String(attribute || 'filter')
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

function prepareFacetContainer(container, attribute) {
  if (!container) {
    return container;
  }

  container.classList.add('search-page__sidebar-block');
  container.setAttribute('data-panel-key', getPanelKey(attribute));

  return container;
}

function formatFacetValue(attribute, value, config) {
  if (value == null) {
    return value;
  }

  if (attribute === config.attributes.size) {
    if (/^\d+(\.\d+)?$/.test(value.trim())) {
      return `${value} inseam`;
    }
    if (typeof window.formattedSize === 'function') {
      return window.formattedSize(value);
    }
  }

  if (attribute === config.attributes.type) {
    return getCategoryTagLabel(value) || value;
  }

  return value;
}

function formatCurrentRefinementItem(item, config) {
  return {
    ...item,
    label: getAttributeLabel(config, item.attribute),
    refinements: Array.isArray(item.refinements)
      ? item.refinements.map((refinement) => ({
        ...refinement,
        label: formatFacetValue(item.attribute, refinement.label, config),
      }))
      : item.refinements,
  };
}

function isColorAttribute(attribute, config) {
  return attribute === config.attributes.color || attribute === 'meta.custom.color_group';
}

function getSwatchColor(colorSwatchMap, colorName) {
  if (!colorSwatchMap || !colorName) return null;
  return colorSwatchMap[String(colorName).toLowerCase().trim()] || null;
}

function isValidColorFacetItem(item, colorSwatchMap) {
  const facetValue = getFacetItemValue(item);

  if (typeof facetValue !== 'string') {
    return false;
  }

  const normalizedValue = facetValue.trim();

  if (!normalizedValue || normalizedValue === '#REF!') {
    return false;
  }

  return Boolean(getSwatchColor(colorSwatchMap, normalizedValue));
}

function renderColorFacetItem(item, { html }) {
  const hex = item.swatchColor;
  return html`
    <label class="ais-RefinementList-label">
      <input type="checkbox" class="ais-RefinementList-checkbox" value="${item.value}" checked=${item.isRefined} />
      ${hex ? html`<span class="ais-RefinementList-colorSwatch" style="background-color: ${hex}"></span>` : ''}
      <span class="ais-RefinementList-labelText">${item.highlighted}</span>
      <span class="ais-RefinementList-count">${item.count}</span>
    </label>
  `;
}

function createRefinementPanel({ container, attribute, label, config, isVisibleItem = () => true, colorSwatchMap }) {
  const isTypeAttribute = attribute === config.attributes.type;
  const isColorFacet = Boolean(colorSwatchMap && Object.keys(colorSwatchMap).length);

  return panel({
    templates: {
      header: renderPanelHeader(label),
    },
    hidden: ({ results }) => {
      if (!hasFacetResults(results, attribute)) {
        return true;
      }

      try {
        const facetValues = results.getFacetValues(attribute);
        const items = Array.isArray(facetValues) ? facetValues : [facetValues].filter(Boolean);

        return !items.some(isVisibleItem);
      } catch (error) {
        void error;
        return true;
      }
    },
  })(refinementList)({
    container,
    attribute,
    operator: 'or',
    limit: isTypeAttribute ? 100 : 8,
    showMore: true,
    showMoreLimit: isTypeAttribute ? 200 : 20,
    ...(isColorFacet ? { templates: { item: renderColorFacetItem } } : {}),
    transformItems(items) {
      return items
        .filter(isVisibleItem)
        .map((item) => {
          const formatted = formatFacetValue(attribute, item.label, config);

          return {
            ...item,
            label: formatted,
            highlighted: formatted,
            ...(isColorFacet ? { swatchColor: getSwatchColor(colorSwatchMap, item.label) } : {}),
          };
        });
    },
  });
}

function createAvailabilityPanel({ container, attribute, label, optionLabel }) {
  return panel({
    templates: {
      header: renderPanelHeader(label),
    },
    hidden: ({ results }) => !hasFacetResults(results, attribute),
  })(toggleRefinement)({
    container,
    attribute,
    on: true,
    templates: {
      labelText: optionLabel,
    },
  });
}

function renderPriceSlider(container, { range, start, refine }) {
  const min = range.min != null ? range.min : 0;
  const max = range.max != null ? range.max : 1000;

  if (min === max) return;

  const currentMin = start[0] != null && start[0] !== -Infinity ? start[0] : min;
  const currentMax = start[1] != null && start[1] !== Infinity ? start[1] : max;

  container._priceSliderRefine = refine;
  container._priceSliderMin = min;
  container._priceSliderMax = max;

  let track = container.querySelector('.price-slider__track');

  if (!track) {
    container.innerHTML = `
      <div class="price-slider">
        <div class="price-slider__runway">
          <div class="price-slider__track">
            <div class="price-slider__fill"></div>
          </div>
          <input type="range" class="price-slider__input price-slider__input--min" aria-label="Minimum price">
          <input type="range" class="price-slider__input price-slider__input--max" aria-label="Maximum price">
        </div>
        <div class="price-slider__values">
          <span class="price-slider__value price-slider__value--min"></span>
          <span class="price-slider__value price-slider__value--max"></span>
        </div>
      </div>
    `;

    const inputMin = container.querySelector('.price-slider__input--min');
    const inputMax = container.querySelector('.price-slider__input--max');

    const commitRefine = () => {
      const lo = Number(inputMin.value);
      const hi = Number(inputMax.value);
      const rangeMin = container._priceSliderMin;
      const rangeMax = container._priceSliderMax;
      container._priceSliderRefine([
        lo <= rangeMin ? undefined : lo,
        hi >= rangeMax ? undefined : hi,
      ]);
    };

    inputMin.addEventListener('input', () => {
      if (Number(inputMin.value) > Number(inputMax.value)) {
        inputMin.value = inputMax.value;
      }
      updateSliderUI(container);
    });

    inputMax.addEventListener('input', () => {
      if (Number(inputMax.value) < Number(inputMin.value)) {
        inputMax.value = inputMin.value;
      }
      updateSliderUI(container);
    });

    inputMin.addEventListener('change', commitRefine);
    inputMax.addEventListener('change', commitRefine);
  }

  const inputMin = container.querySelector('.price-slider__input--min');
  const inputMax = container.querySelector('.price-slider__input--max');

  inputMin.min = min;
  inputMin.max = max;
  inputMin.value = currentMin;

  inputMax.min = min;
  inputMax.max = max;
  inputMax.value = currentMax;

  updateSliderUI(container);
}

function updateSliderUI(container) {
  const inputMin = container.querySelector('.price-slider__input--min');
  const inputMax = container.querySelector('.price-slider__input--max');
  const fill = container.querySelector('.price-slider__fill');
  const labelMin = container.querySelector('.price-slider__value--min');
  const labelMax = container.querySelector('.price-slider__value--max');

  if (!inputMin || !inputMax) return;

  const min = container._priceSliderMin || 0;
  const max = container._priceSliderMax || 1000;
  const lo = Number(inputMin.value);
  const hi = Number(inputMax.value);
  const span = max - min || 1;
  const leftPct = ((lo - min) / span) * 100;
  const rightPct = ((hi - min) / span) * 100;

  if (fill) {
    fill.style.left = `${leftPct}%`;
    fill.style.right = `${100 - rightPct}%`;
  }

  if (labelMin) {
    labelMin.textContent = `$ ${Math.round(lo)}`;
    labelMin.style.left = `${leftPct}%`;
    labelMin.style.transform = `translateX(-${leftPct}%)`;
  }

  if (labelMax) {
    labelMax.textContent = `$ ${Math.round(hi)}`;
    labelMax.style.left = `${rightPct}%`;
    labelMax.style.transform = `translateX(-${rightPct}%)`;
  }
}

function createPricePanel({ container, attribute, label }) {
  const priceSliderWidget = connectRange((renderOptions) => {
    const { range, start, refine, widgetParams } = renderOptions;
    const target = widgetParams.container;

    if (!target) return;

    renderPriceSlider(target.querySelector('.ais-Panel-body') || target, {
      range,
      start,
      refine,
    });
  });

  const wrappedContainer = document.createElement('div');
  container.appendChild(wrappedContainer);

  wrappedContainer.innerHTML = `
    <div class="ais-Panel">
      <div class="ais-Panel-header">${renderPanelHeader(label)}</div>
      <div class="ais-Panel-body"></div>
    </div>
  `;

  return priceSliderWidget({
    container: wrappedContainer,
    attribute,
    precision: 0,
  });
}

function createFacetWidget({ container, attribute, config }) {
  const label = getAttributeLabel(config, attribute);
  const widgetType = resolveWidgetType(attribute, config);
  const preparedContainer = prepareFacetContainer(container, attribute);

  console.log('[Search Filters] Creating widget:', { attribute, label, widgetType });

  if (!preparedContainer) {
    console.warn('[Search Filters] Missing container for attribute:', attribute);
    return refinementList({ container: document.createElement('div'), attribute });
  }

  if (widgetType === 'rangeInput') {
    return createPricePanel({ container: preparedContainer, attribute, label });
  }

  if (widgetType === 'toggleRefinement') {
    return createAvailabilityPanel({
      container: preparedContainer,
      attribute,
      label,
      optionLabel: config.labels.availabilityOption || 'In stock only',
    });
  }

  const isTypeAttribute = attribute === config.attributes.type;
  const isVisibleItem = isTypeAttribute
    ? (item) => Boolean(getCategoryTagLabel(getFacetItemValue(item)))
    : isColorAttribute(attribute, config)
      ? (item) => isValidColorFacetItem(item, config.colorSwatchMap)
      : () => true;

  return createRefinementPanel({
    container: preparedContainer,
    attribute,
    label,
    config,
    isVisibleItem,
    colorSwatchMap: isColorAttribute(attribute, config) ? config.colorSwatchMap : null,
  });
}

function getKnownFacetFactories(config) {
  const knownAttributes = [
    config.attributes.productType,
    config.attributes.type,
    config.attributes.color,
    config.attributes.size,
    config.attributes.availability,
    config.attributes.price,
  ].filter(Boolean);

  console.log('[Search Filters] Known facet attributes:', knownAttributes);

  return knownAttributes.map((attribute) => (container) =>
    createFacetWidget({ container, attribute, config })
  );
}

export function createWidgets(root, config) {
  const sidebarContainer = root.querySelector(SELECTORS.sidebar);
  const hitsContainer = root.querySelector(SELECTORS.hits);

  bindSwatchEvents(hitsContainer);

  const widgets = [
    createNoResultsToggle(root),
    createInsightsIndexTracker(root, config),
    createHitsViewedPusher(),
    createColorRefinementTracker(config),
    configure({
      hitsPerPage: config.hitsPerPage,
      clickAnalytics: true,
      facetingAfterDistinct: false,
      maxValuesPerFacet: 1000,
      ...(config.filters ? { filters: config.filters } : {}),
    }),
    createVirtualSearchBox({}),
    stats({
      container: root.querySelector(SELECTORS.stats),
      templates: {
        text: renderStatsText,
      },
    }),
    currentRefinements({
      container: root.querySelector(SELECTORS.currentRefinements),
      transformItems(items) {
        return items.map((item) => formatCurrentRefinementItem(item, config));
      },
    }),
    clearRefinements({
      container: root.querySelector(SELECTORS.clearRefinements),
      templates: {
        resetLabel: 'Clear all filters',
      },
    }),
    hits({
      container: hitsContainer,
      templates: {
        item(hit) {
          if (hit.__position === 1) {
            resetHitRenderIndex();
          }
          return renderProductHit(hit, config, _activeColorGroups);
        },
        empty(results) {
          return renderEmptyState(results, config);
        },
      },
    }),
    pagination({
      container: root.querySelector(SELECTORS.pagination),
      padding: 2,
      showFirst: false,
      showLast: false,
    }),
    dynamicWidgets({
      container: sidebarContainer,
      facets: ['*'],
      transformItems(items, { results }) {
        const rawResult = results && results._rawResults && results._rawResults[0];
        const facetOrdering = rawResult && rawResult.renderingContent && rawResult.renderingContent.facetOrdering;
        const facetOrder = facetOrdering && facetOrdering.facets && facetOrdering.facets.order;
        const hasAlgoliaOrdering = Boolean(facetOrder && facetOrder.length);

        if (hasAlgoliaOrdering) {
          console.log('[Search Filters] Facet order provided by Algolia Facet Display:', items);
          return items;
        }

        const knownAttributes = [
          config.attributes.productType,
          config.attributes.type,
          config.attributes.color,
          config.attributes.size,
          config.attributes.availability,
          config.attributes.price,
        ].filter(Boolean);

        const itemsSet = new Set(items);
        const knownSet = new Set(knownAttributes);
        const discovered = new Set();

        items.forEach((attr) => {
          if (!knownSet.has(attr)) {
            discovered.add(attr);
          }
        });

        if (results && results._rawResults) {
          results._rawResults.forEach((raw) => {
            if (raw.facets) {
              Object.keys(raw.facets).forEach((key) => {
                if (!knownSet.has(key)) {
                  discovered.add(key);
                }
              });
            }
          });
        }

        const knownPresent = knownAttributes.filter((attr) => itemsSet.has(attr));
        const discoveredSorted = [...discovered].sort();
        const allAttributes = knownPresent.concat(discoveredSorted);

        console.log('[Search Filters] No Algolia Facet Display ordering. Fallback — known:', knownPresent, 'discovered:', discoveredSorted);
        return allAttributes;
      },
      widgets: getKnownFacetFactories(config),
      fallbackWidget({ container, attribute }) {
        console.log('[Search Filters] Fallback widget for unknown facet:', attribute);
        return createFacetWidget({ container, attribute, config });
      },
    }),
  ];

  if (config.sortBy.length > 1) {
    const sortShell = root.querySelector(SELECTORS.sortShell);

    const customSortBy = connectSortBy((renderOptions) => {
      const { options, currentRefinement, refine } = renderOptions;

      if (!sortShell) return;

      const trigger = sortShell.querySelector('.search-page__sort-trigger');
      const dropdown = sortShell.querySelector('.search-page__sort-dropdown');

      if (!trigger || !dropdown) return;

      const currentLabel = (options.find((o) => o.value === currentRefinement) || options[0] || {}).label || 'Sort';
      trigger.querySelector('.search-page__sort-trigger-label').textContent = currentLabel;

      dropdown.innerHTML = options
        .map(
          (option) =>
            `<button type="button" class="search-page__sort-option${option.value === currentRefinement ? ' is-active' : ''}" data-value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</button>`
        )
        .join('');

      dropdown.querySelectorAll('.search-page__sort-option').forEach((btn) => {
        btn.addEventListener('click', () => {
          refine(btn.dataset.value);
          sortShell.classList.remove('is-open');
        });
      });
    });

    if (sortShell) {
      const trigger = sortShell.querySelector('.search-page__sort-trigger');
      if (trigger) {
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          sortShell.classList.toggle('is-open');
        });

        document.addEventListener('click', (e) => {
          if (!sortShell.contains(e.target)) {
            sortShell.classList.remove('is-open');
          }
        });
      }
    }

    widgets.push(
      customSortBy({
        container: document.createElement('div'),
        items: config.sortBy,
      })
    );
  } else {
    const sortContainer = root.querySelector(SELECTORS.sortBy);
    const sortShell = root.querySelector(SELECTORS.sortShell);

    if (sortContainer) {
      sortContainer.classList.add('search-page__sort--hidden');
    }

    if (sortShell) {
      sortShell.classList.add('search-page__sort-shell--hidden');
    }
  }

  return widgets;
}
